import { useEffect, useRef, useState, type ReactElement } from 'react'
import { useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { useMetroMapStore } from '../state/useMetroMapStore'
import styles from './NightTransitionLoader.module.css'

/** Default cosmetic hold duration (ms) — the "loading moment" on web. */
export const NIGHT_TRANSITION_HOLD_MS = 300

export interface NightTransitionLoaderProps {
  /** Cosmetic hold duration. Test seam; defaults to {@link NIGHT_TRANSITION_HOLD_MS}. */
  readonly holdMs?: number
}

/**
 * Brief cosmetic loading overlay shown on a Night Tube mode FLIP
 * (TASK-412 M / P2).
 *
 * Web's night toggle is instant (geometry pre-cached per theme + a
 * draw-time visibility filter — no async rebuild, so no real
 * build-complete signal). To keep the loading moment visible on web like
 * the other clients, this overlay animates in on a `currentMode` change,
 * holds ~300ms while the instant theme/filter swap happens underneath,
 * then animates out — NOT gated on a real signal. Spinner tint =
 * `--mm-color-text-secondary`.
 *
 * Mounted once at AppShell. Renders the overlay only during the brief
 * transition window; otherwise renders nothing.
 */
export function NightTransitionLoader({
  holdMs = NIGHT_TRANSITION_HOLD_MS,
}: NightTransitionLoaderProps = {}): ReactElement | null {
  const { t } = useTranslation('common')
  const store = useMetroMapStore()
  const currentMode = useSyncExternalStore(store.subscribe, () => store.getState().currentMode)

  const [visible, setVisible] = useState(false)
  const prevModeRef = useRef(currentMode)

  // Show on an actual mode FLIP (not the initial mount), then auto-hide
  // after the cosmetic hold. House pattern: the show-latch lives in an
  // effect keyed on `currentMode` (cf. BubbleOverlay / RouteBubbleOverlay).
  useEffect(() => {
    if (prevModeRef.current === currentMode) return
    prevModeRef.current = currentMode
    setVisible(true)
    const id = window.setTimeout(() => setVisible(false), holdMs)
    return () => window.clearTimeout(id)
  }, [currentMode, holdMs])

  if (!visible) return null

  return (
    <div
      className={`${styles.overlay} ${styles.overlayVisible}`}
      data-testid="night-transition-loader"
      role="status"
      aria-live="polite"
      aria-label={t('ui.modes.switching', { defaultValue: 'Switching map mode' })}
    >
      <div className={styles.spinner} />
    </div>
  )
}
